import React, { useState, useEffect } from 'react';
import { Search as SearchIcon, Play, Heart, Filter, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import SongMenu from '../SongMenu';

const SearchEnhanced = ({ playSong, songs, vibes, likedSongs, toggleLike, user }) => {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ songs: [], vibes: [], total_songs: 0, total_vibes: 0 });
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [genres, setGenres] = useState([]);
  const [tags, setTags] = useState([]);
  
  // Filters
  const [selectedVibes, setSelectedVibes] = useState([]);
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  // Fetch genres and tags on mount
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        // Fetch genres
        const genresResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/genres`);
        if (genresResponse.ok) {
          const genresData = await genresResponse.json();
          setGenres(genresData);
        }
        
        // Fetch tags
        const tagsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tags`);
        if (tagsResponse.ok) {
          const tagsData = await tagsResponse.json();
          setTags(tagsData.map(t => t.name));
        }
      } catch (error) {
        console.error('Failed to fetch filters:', error);
      }
    };

    fetchFilters();
  }, []);

  const handleSearch = async () => {
    if (!query.trim() && selectedVibes.length === 0 && selectedGenres.length === 0 && selectedTags.length === 0) {
      setSearchResults({ songs: [], vibes: [], total_songs: 0, total_vibes: 0 });
      return;
    }

    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      if (query.trim()) params.append('q', query.trim());
      if (selectedVibes.length > 0) params.append('vibes', selectedVibes.join(','));
      if (selectedGenres.length > 0) params.append('genres', selectedGenres.join(','));
      if (selectedTags.length > 0) params.append('tags', selectedTags.join(','));

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/search?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
        console.log('✅ Search results:', data.total_songs, 'songs,', data.total_vibes, 'vibes');
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch();
    }, 300);

    return () => clearTimeout(timer);
  }, [query, selectedVibes, selectedGenres, selectedTags]);

  const toggleFilter = (filterType, value) => {
    if (filterType === 'vibe') {
      setSelectedVibes(prev => 
        prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
      );
    } else if (filterType === 'genre') {
      setSelectedGenres(prev => 
        prev.includes(value) ? prev.filter(g => g !== value) : [...prev, value]
      );
    } else if (filterType === 'tag') {
      setSelectedTags(prev => 
        prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]
      );
    }
  };

  const clearAllFilters = () => {
    setSelectedVibes([]);
    setSelectedGenres([]);
    setSelectedTags([]);
  };

  const activeFiltersCount = selectedVibes.length + selectedGenres.length + selectedTags.length;

  const recentSearches = ['Abundance', 'Meditation', 'Main Character', 'Rave'];

  return (
    <div className="page-content">
      <div className="search-container">
        {/* Search Input */}
        <div className="search-input-container" style={{ position: 'relative', marginBottom: '24px' }}>
          <SearchIcon size={20} className="search-icon" style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
          <input
            type="text"
            placeholder="Search songs, artists, albums..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="search-input"
            style={{
              width: '100%',
              padding: '16px 60px 16px 48px',
              borderRadius: '24px',
              border: '2px solid #ddd',
              fontSize: '16px',
              outline: 'none'
            }}
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: activeFiltersCount > 0 ? '#9D4EDD' : '#f0f0f0',
              color: activeFiltersCount > 0 ? 'white' : '#666',
              border: 'none',
              borderRadius: '12px',
              padding: '8px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            <Filter size={16} />
            {activeFiltersCount > 0 && <span>{activeFiltersCount}</span>}
          </button>
        </div>

        {/* Original Music Notice */}
        <div style={{
          textAlign: 'center',
          padding: '12px',
          marginBottom: '20px',
          fontSize: '14px',
          color: '#666',
          fontStyle: 'italic'
        }}>
          All songs are original and produced by MetaSoul® Music
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div style={{
            background: '#f9f9f9',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600' }}>Filters</h3>
              {activeFiltersCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#9D4EDD',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}
                >
                  Clear All
                </button>
              )}
            </div>

            {/* Vibes Filter */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#666' }}>Vibes</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {vibes.slice(0, 10).map(vibe => (
                  <button
                    key={vibe.id}
                    onClick={() => toggleFilter('vibe', vibe.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '16px',
                      border: selectedVibes.includes(vibe.id) ? '2px solid #9D4EDD' : '2px solid #ddd',
                      background: selectedVibes.includes(vibe.id) ? '#f3e8ff' : 'white',
                      color: selectedVibes.includes(vibe.id) ? '#9D4EDD' : '#666',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}
                  >
                    {vibe.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Genres Filter */}
            <div style={{ marginBottom: '16px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#666' }}>Genres</h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {genres.slice(0, 10).map(genre => (
                  <button
                    key={genre.id}
                    onClick={() => toggleFilter('genre', genre.slug)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '16px',
                      border: selectedGenres.includes(genre.slug) ? '2px solid #9D4EDD' : '2px solid #ddd',
                      background: selectedGenres.includes(genre.slug) ? '#f3e8ff' : 'white',
                      color: selectedGenres.includes(genre.slug) ? '#9D4EDD' : '#666',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}
                  >
                    {genre.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Tags Filter */}
            {tags.length > 0 && (
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#666' }}>Tags</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {tags.slice(0, 15).map(tag => (
                    <button
                      key={tag}
                      onClick={() => toggleFilter('tag', tag)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '16px',
                        border: selectedTags.includes(tag) ? '2px solid #9D4EDD' : '2px solid #ddd',
                        background: selectedTags.includes(tag) ? '#f3e8ff' : 'white',
                        color: selectedTags.includes(tag) ? '#9D4EDD' : '#666',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600'
                      }}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            Searching...
          </div>
        )}

        {/* No Query State */}
        {!query && activeFiltersCount === 0 && !loading ? (
          <>
            {/* Recent Searches */}
            <section className="content-section" style={{ marginBottom: '32px' }}>
              <h2 style={{ marginBottom: '16px' }}>Recent Searches</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {recentSearches.map((search, index) => (
                  <button
                    key={index}
                    onClick={() => setQuery(search)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px 20px',
                      borderRadius: '24px',
                      border: 'none',
                      background: '#f0f0f0',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#333',
                      fontWeight: '500'
                    }}
                  >
                    <SearchIcon size={16} color="#666" />
                    <span>{search}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* Browse Vibes */}
            <section className="content-section">
              <h2 style={{ marginBottom: '16px' }}>Browse All Vibes</h2>
              <div className="vibes-grid">
                {vibes.map(vibe => (
                  <Link
                    key={vibe.id}
                    to={`/vibe/${vibe.id}`}
                    className="vibe-card"
                    style={{
                      backgroundColor: vibe.color,
                      backgroundImage: vibe.image_url ? `url(${vibe.image_url})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      position: 'relative',
                      minHeight: '180px',
                      borderRadius: '12px',
                      overflow: 'hidden',
                      cursor: 'pointer'
                    }}
                  >
                    {vibe.image_url && (
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)'
                      }} />
                    )}
                    <div style={{ position: 'relative', zIndex: 1, padding: '20px' }}>
                      <h3 style={{ color: 'white', marginBottom: '8px' }}>{vibe.name}</h3>
                      <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px' }}>{vibe.songCount} songs</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          </>
        ) : !loading && (
          <>
            {/* Search Results */}
            {searchResults.songs.length > 0 && (
              <section className="content-section" style={{ marginBottom: '32px' }}>
                <h2 style={{ marginBottom: '16px' }}>Songs ({searchResults.total_songs})</h2>
                <div className="songs-grid">
                  {searchResults.songs.map(song => (
                    <div key={song.id} className="song-card">
                      <div className="song-card-image">
                        <img src={song.albumArt || song.album_art_url} alt={song.title} />
                        <SongMenu song={song} user={user} />
                        <button 
                          className="play-button"
                          onClick={() => playSong(song, searchResults.songs)}
                        >
                          <Play size={20} fill="white" />
                        </button>
                      </div>
                      <div className="song-card-content">
                        <h3 className="song-title">{song.title}</h3>
                        <p className="song-artist">{song.artist}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {searchResults.vibes.length > 0 && (
              <section className="content-section">
                <h2 style={{ marginBottom: '16px' }}>Vibes ({searchResults.total_vibes})</h2>
                <div className="vibes-grid">
                  {searchResults.vibes.map(vibe => (
                    <Link
                      key={vibe.id}
                      to={`/vibe/${vibe.id}`}
                      className="vibe-card"
                      style={{
                        backgroundColor: vibe.color,
                        backgroundImage: vibe.image_url ? `url(${vibe.image_url})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        position: 'relative',
                        minHeight: '180px',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        padding: '20px'
                      }}
                    >
                      {vibe.image_url && (
                        <div style={{
                          position: 'absolute',
                          inset: 0,
                          background: 'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(0,0,0,0.7) 100%)',
                          zIndex: 0
                        }} />
                      )}
                      <div style={{ position: 'relative', zIndex: 1 }}>
                        <h3 style={{ color: 'white', marginBottom: '4px', fontSize: '18px', fontWeight: '600' }}>{vibe.name}</h3>
                        <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', margin: 0 }}>{vibe.song_count} songs</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {searchResults.songs.length === 0 && searchResults.vibes.length === 0 && (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
                <SearchIcon size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                <h3 style={{ fontSize: '20px', marginBottom: '8px' }}>No results found</h3>
                <p>Try adjusting your search or filters</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchEnhanced;
